import { useCallback, useEffect, useRef, useState } from "react";
import type { Charge, Event, Mode, Period, State } from "../types";

export type ChargePop = {
  key: number;
  charge: Charge;
};

export type Hook = {
  budgetId: string | null;
  mode: Mode;
  setMode: (partial: Partial<Mode>) => void;
  connected: boolean;
  period: Period | null;
  charges: Charge[];
  serverOffsetMs: number;
  pops: ChargePop[];
  consumePop: (key: number) => void;
  setBudget: (body: {
    period: string;
    income_cents: number;
    label?: string;
    start_at?: string;
    end_at?: string;
  }) => Promise<void>;
  addCharge: (amount_cents: number, merchant: string) => Promise<void>;
  deleteCharge: (id: number) => Promise<void>;
};

function readUrl(): { id: string | null; mode: Mode } {
  const u = new URL(window.location.href);
  return {
    id: u.searchParams.get("id"),
    mode: {
      kiosk: u.searchParams.get("kiosk") === "1",
      led: u.searchParams.get("led") === "1",
    },
  };
}

function setUrlId(id: string) {
  const u = new URL(window.location.href);
  u.searchParams.set("id", id);
  window.history.replaceState({}, "", u.toString());
}

function setUrlFlag(key: "kiosk" | "led", on: boolean) {
  const u = new URL(window.location.href);
  if (on) u.searchParams.set(key, "1");
  else u.searchParams.delete(key);
  window.history.replaceState({}, "", u.toString());
}

export function useCashclock(): Hook {
  const [budgetId, setBudgetId] = useState<string | null>(() => readUrl().id);
  const [mode, setModeState] = useState<Mode>(() => readUrl().mode);

  const setMode = useCallback((partial: Partial<Mode>) => {
    setModeState((prev) => {
      const next = { ...prev, ...partial };
      if (partial.kiosk !== undefined) setUrlFlag("kiosk", next.kiosk);
      if (partial.led !== undefined) setUrlFlag("led", next.led);
      return next;
    });
  }, []);
  const [period, setPeriod] = useState<Period | null>(null);
  const [charges, setCharges] = useState<Charge[]>([]);
  const [connected, setConnected] = useState(false);
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [pops, setPops] = useState<ChargePop[]>([]);
  const popKey = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);

  const applyState = useCallback((s: State) => {
    setPeriod(s.period ?? null);
    setCharges(s.charges ?? []);
    const server = new Date(s.server_time).getTime();
    setServerOffsetMs(server - Date.now());
  }, []);

  // Fetch state + open WS whenever budgetId changes.
  useEffect(() => {
    if (!budgetId) {
      setPeriod(null);
      setCharges([]);
      return;
    }

    let cancelled = false;

    fetch(`/api/budgets/${encodeURIComponent(budgetId)}`)
      .then((r) => r.json())
      .then((s: State) => {
        if (!cancelled) applyState(s);
      })
      .catch(() => {});

    const connect = () => {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(
        `${proto}//${window.location.host}/api/budgets/${encodeURIComponent(budgetId)}/ws`
      );
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        setTimeout(() => {
          if (!cancelled) connect();
        }, 1500);
      };
      ws.onmessage = (e) => {
        const ev: Event = JSON.parse(e.data);
        if (ev.type === "state") {
          applyState(ev);
        } else if (ev.type === "charge") {
          setCharges((prev) => [ev.charge, ...prev]);
          const k = ++popKey.current;
          setPops((p) => [...p, { key: k, charge: ev.charge }]);
        } else if (ev.type === "charge_deleted") {
          setCharges((prev) => prev.filter((c) => c.id !== ev.id));
        }
      };
    };
    connect();

    return () => {
      cancelled = true;
      wsRef.current?.close();
    };
  }, [budgetId, applyState]);

  const consumePop = useCallback((key: number) => {
    setPops((p) => p.filter((x) => x.key !== key));
  }, []);

  const setBudget = useCallback(
    async (body: {
      period: string;
      income_cents: number;
      label?: string;
      start_at?: string;
      end_at?: string;
    }) => {
      const r = await fetch("/api/budget", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      const p: Period = await r.json();
      setUrlId(p.id);
      setBudgetId(p.id);
    },
    []
  );

  const addCharge = useCallback(
    async (amount_cents: number, merchant: string) => {
      if (!budgetId) throw new Error("no budget yet");
      const r = await fetch(`/api/budgets/${encodeURIComponent(budgetId)}/charges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount_cents, merchant }),
      });
      if (!r.ok) throw new Error(await r.text());
    },
    [budgetId]
  );

  const deleteCharge = useCallback(
    async (id: number) => {
      if (!budgetId) throw new Error("no budget yet");
      const r = await fetch(`/api/budgets/${encodeURIComponent(budgetId)}/charges/${id}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error(await r.text());
    },
    [budgetId]
  );

  return {
    budgetId,
    mode,
    setMode,
    connected,
    period,
    charges,
    serverOffsetMs,
    pops,
    consumePop,
    setBudget,
    addCharge,
    deleteCharge,
  };
}
